import { faChevronRight, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from 'react';

import Button from '@/components/Button';
import Card from '@/components/Card';
import Chip from '@/components/Chip';
import EmptyState from '@/components/EmptyState';
import IconicButton from '@/components/IconicButton';
import LoadingState from '@/components/LoadingState';
import Modal from '@/components/Modal';
import Page from '@/components/Page';
import Skeleton from '@/components/Skeleton';
import Stack from '@/components/Stack';
import Table from '@/components/Table';
import TableHeader from '@/components/TableHeader';
import { Body, Heading, Small } from '@/components/Typography';
import { TableCell, TableRow } from '@/elements/Table';
import Link from '@/elements/Link';
import ROUTES from '@/routes/routes';

import {
  useAccountDetail,
  useAddLineItem,
  useCreateInvoice,
  useCycleAccounts,
  useCycles,
  useInvoiceDetail,
  useReconcile,
  useRemoveLineItem,
  useVoidInvoice,
} from './hooks/useInvoiceHealth';
import type {
  ReconcileAction,
  ReconcilePreview,
} from './hooks/useInvoiceHealth';
import type {
  CycleAccount,
  CycleTimesheet,
  DeepAccountDetail,
  DeepInvoiceDetail,
  InvoiceCycle,
} from './types';

const humanizeStatus = (status: string) =>
  status.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());

const humanizeLabel = (value: string) =>
  value.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());

const ONE_WEEK_IN_DAYS = 7;

const formatDateTime = (value: string | null) => {
  if (!value) return 'n/a';

  return new Date(value).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatCurrency = (amountCents: number | null) => {
  if (amountCents == null) return 'n/a';

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amountCents / 100);
};

const statusColor = (
  status: string,
): 'success' | 'warning' | 'error' | 'default' => {
  if (status === 'included') return 'success';
  if (status === 'not_included') return 'warning';
  if (
    status === 'linked_to_other_invoice' ||
    status === 'requires_review' ||
    status === 'rejected' ||
    status === 'not_invoiceable' ||
    status === 'held'
  ) {
    return 'error';
  }

  return 'default';
};

const shiftIsoDate = (value: string | null, days: number) => {
  if (!value) return null;

  const shifted = new Date(value);
  shifted.setDate(shifted.getDate() + days);
  return shifted.toISOString();
};

const formatCycleDateLabel = (value: string) =>
  new Date(value).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

const formatCycleDayLabel = (value: string) =>
  new Date(value).toLocaleDateString('en-US', {
    weekday: 'long',
  });

const linkedTextCss = {
  color: '#16a34a',
  textDecoration: 'underline',
  fontWeight: 600,
};

const INVOICE_HEALTH_PASSWORD = 'gravydevs123';
const INVOICE_HEALTH_AUTH_KEY = 'invoiceHealthAuth';

const buildPreviewCycle = (cycle: InvoiceCycle): InvoiceCycle => {
  const startedAt = shiftIsoDate(cycle.started_at, ONE_WEEK_IN_DAYS)!;
  const endedAt = shiftIsoDate(cycle.ended_at, ONE_WEEK_IN_DAYS);
  const from = shiftIsoDate(cycle.from, ONE_WEEK_IN_DAYS)!;
  const to = shiftIsoDate(cycle.to, ONE_WEEK_IN_DAYS)!;

  return {
    ...cycle,
    id: cycle.id * -1,
    status: 'preview',
    started_at: startedAt,
    ended_at: endedAt,
    date_label: formatCycleDateLabel(startedAt),
    day_label: formatCycleDayLabel(startedAt),
    invoice_count: 0,
    timesheet_count: 0,
    failed_account_count: 0,
    from,
    to,
    is_preview: true,
    preview_base_cycle_id: cycle.id,
    preview_base_label: `${cycle.date_label} (${cycle.day_label})`,
  };
};

type AccountFilter = 'all' | 'complete' | 'incomplete' | 'no_invoice';

type CycleDropdownProps = {
  cycles: InvoiceCycle[];
  selectedCycle: InvoiceCycle | null;
  onSelect: (cycle: InvoiceCycle) => void;
};

const CycleDropdown: React.FC<CycleDropdownProps> = ({
  cycles,
  selectedCycle,
  onSelect,
}) => {
  const selected = selectedCycle;
  const previewBaseCycle =
    selected?.is_preview && selected.preview_base_cycle_id != null
      ? cycles.find((cycle) => cycle.id === selected.preview_base_cycle_id) ||
        cycles[0] ||
        null
      : selected && !selected.is_preview
        ? selected
        : cycles[0] || null;
  const previewCycle = previewBaseCycle
    ? buildPreviewCycle(previewBaseCycle)
    : null;
  const selectableCycles = previewCycle
    ? [
        previewCycle,
        ...cycles,
        ...(selected?.is_preview && selected.id !== previewCycle.id
          ? [selected]
          : []),
      ]
    : cycles;

  return (
    <Stack vertical gap={8}>
      <div style={{ minWidth: 320 }}>
        <select
          value={selected?.id ?? ''}
          onChange={(event) => {
            const cycle = selectableCycles.find(
              (entry) => entry.id === Number(event.target.value),
            );
            if (cycle) onSelect(cycle);
          }}
          style={{
            width: '100%',
            padding: '10px 14px',
            borderRadius: 8,
            border: '1px solid #d0d5dd',
            background: '#fff',
            fontSize: 14,
            fontWeight: 500,
            color: '#344054',
            cursor: 'pointer',
            appearance: 'none',
          }}
        >
          <option value="" disabled>
            Select an invoice cycle...
          </option>
          {selectableCycles.map((cycle) => (
            <option key={cycle.id} value={cycle.id}>
              {cycle.is_preview ? 'Future: ' : ''}
              {cycle.date_label} ({cycle.day_label})
            </option>
          ))}
        </select>
      </div>

      {selected && (
        <Stack gap={8} align="center" wrap>
          {selected.is_preview ? (
            <Small>
              Previewing next week&apos;s run based on{' '}
              {selected.preview_base_label || 'the selected cycle'}
            </Small>
          ) : (
            <>
              <Small>
                {selected.invoice_count} invoices and {selected.timesheet_count}{' '}
                timesheets in cycle
              </Small>
              {selected.failed_account_count > 0 && (
                <div style={{ color: '#b42318', fontSize: 12 }}>
                  {selected.failed_account_count} failed accounts
                </div>
              )}
            </>
          )}
        </Stack>
      )}
    </Stack>
  );
};

type ConfirmState = {
  accountName: string;
  timesheet: CycleTimesheet;
} | null;

type ConfirmModalProps = {
  confirmState: ConfirmState;
  loading: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
};

const ConfirmSyncModal: React.FC<ConfirmModalProps> = ({
  confirmState,
  loading,
  error,
  onCancel,
  onConfirm,
}) => {
  if (!confirmState) return null;

  return (
    <Modal
      displayFlex
      size="xs"
      title="Add line item"
      onRequestClose={loading ? undefined : onCancel}
      wrapperBackground
    >
      <Card>
        <Card.Section>
          <Stack vertical gap={16}>
            <Body weight="semibold">
              Add timesheet {confirmState.timesheet.id} to the invoice for{' '}
              {confirmState.accountName}?
            </Body>
            <Small>
              If the account does not have an invoice yet for this cycle, a new
              invoice will be created first and the line item will be synced
              immediately.
            </Small>
            {error && (
              <div style={{ color: '#b42318', fontSize: 14 }}>{error}</div>
            )}
            <Stack justify="end" gap={12}>
              <Button
                a11yLabel="Cancel"
                appearance="outline"
                label="Cancel"
                disabled={loading}
                type="button"
                onClick={onCancel}
              />
              <Button
                a11yLabel="Confirm add line item"
                label="Add line item"
                isLoading={loading}
                type="button"
                onClick={onConfirm}
              />
            </Stack>
          </Stack>
        </Card.Section>
      </Card>
    </Modal>
  );
};

type ConfirmCreateState = {
  accountName: string;
  accountId: number;
} | null;

type ConfirmCreateModalProps = {
  state: ConfirmCreateState;
  loading: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
};

const ConfirmCreateInvoiceModal: React.FC<ConfirmCreateModalProps> = ({
  state,
  loading,
  error,
  onCancel,
  onConfirm,
}) => {
  if (!state) return null;

  return (
    <Modal
      displayFlex
      size="xs"
      title="Create invoice"
      onRequestClose={loading ? undefined : onCancel}
      wrapperBackground
    >
      <Card>
        <Card.Section>
          <Stack vertical gap={16}>
            <Body weight="semibold">
              Create a new invoice for {state.accountName} (Account{' '}
              {state.accountId})?
            </Body>
            <Small>
              This will create an empty invoice for the selected cycle. You can
              then add line items individually.
            </Small>
            {error && (
              <div style={{ color: '#b42318', fontSize: 14 }}>{error}</div>
            )}
            <Stack justify="end" gap={12}>
              <Button
                a11yLabel="Cancel"
                appearance="outline"
                label="Cancel"
                disabled={loading}
                type="button"
                onClick={onCancel}
              />
              <Button
                a11yLabel="Confirm create invoice"
                label="Create invoice"
                isLoading={loading}
                type="button"
                onClick={onConfirm}
              />
            </Stack>
          </Stack>
        </Card.Section>
      </Card>
    </Modal>
  );
};

type ConfirmVoidState = {
  accountName: string;
  invoiceId: number;
} | null;

type ConfirmVoidModalProps = {
  state: ConfirmVoidState;
  loading: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
};

const ConfirmVoidInvoiceModal: React.FC<ConfirmVoidModalProps> = ({
  state,
  loading,
  error,
  onCancel,
  onConfirm,
}) => {
  if (!state) return null;

  return (
    <Modal
      displayFlex
      size="xs"
      title="Void invoice"
      onRequestClose={loading ? undefined : onCancel}
      wrapperBackground
    >
      <Card>
        <Card.Section>
          <Stack vertical gap={16}>
            <Body weight="semibold">
              Void invoice #{state.invoiceId} for {state.accountName}?
            </Body>
            <Small>
              This will void the invoice in Stripe, reset all linked timesheets
              so they can be re-invoiced, and trigger a new invoice to be
              created.
            </Small>
            {error && (
              <div style={{ color: '#b42318', fontSize: 14 }}>{error}</div>
            )}
            <Stack justify="end" gap={12}>
              <Button
                a11yLabel="Cancel"
                appearance="outline"
                label="Cancel"
                disabled={loading}
                type="button"
                onClick={onCancel}
              />
              <Button
                a11yLabel="Confirm void invoice"
                appearance="outline"
                label="Void invoice"
                isLoading={loading}
                type="button"
                onClick={onConfirm}
              />
            </Stack>
          </Stack>
        </Card.Section>
      </Card>
    </Modal>
  );
};

const ACTION_LABELS: Record<string, string> = {
  void_duplicate: 'Void duplicate invoice',
  fix_totals: 'Sync totals from Stripe',
  items_missing_locally: 'Create missing local items',
  items_orphaned_locally: 'Remove orphaned local items',
  fix_item_amount: 'Fix item amount mismatch',
  stripe_invoice_not_found: 'Stripe invoice not found',
  error: 'Error during reconciliation',
};

const actionLabel = (action: string) =>
  ACTION_LABELS[action] || humanizeLabel(action);

const formatReconcileDetailValue = (value: unknown) => {
  if (value == null) return 'n/a';
  if (Array.isArray(value)) {
    return value.map((entry) => formatReconcileDetailValue(entry)).join(', ');
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
};

type ReconcileModalProps = {
  isOpen: boolean;
  accountName: string | null;
  accountId: number | null;
  preview: ReconcilePreview | null;
  loading: boolean;
  executing: boolean;
  error: string | null;
  onClose: () => void;
  onExecute: () => void;
};

const ReconcileModal: React.FC<ReconcileModalProps> = ({
  isOpen,
  accountName,
  accountId,
  preview,
  loading,
  executing,
  error,
  onClose,
  onExecute,
}) => {
  if (!isOpen) return null;

  const actions = preview?.actions || [];
  const hasActions = actions.length > 0;
  const isExecuted = preview?.mode === 'execute';

  return (
    <Modal
      disableClickout
      size="lg"
      title={`Reconcile ${accountName || `Account ${accountId}`}`}
      onRequestClose={executing ? undefined : onClose}
    >
      <Card>
        <Card.Section>
          <Stack vertical gap={6}>
            <Body weight="semibold">
              {isExecuted
                ? 'Reconciliation completed'
                : 'Review the Stripe sync preview before making changes'}
            </Body>
            <Small>
              {isExecuted
                ? 'Executed actions are listed below so you can review what changed.'
                : 'This checks the selected account against Stripe and proposes safe repair actions before you execute them.'}
            </Small>
          </Stack>
        </Card.Section>

        <Card.Section>
          {loading && (
            <LoadingState text="Scanning invoices against Stripe..." />
          )}
          {!loading && error && (
            <div style={{ color: '#b42318', fontSize: 14 }}>{error}</div>
          )}
          {!loading && preview && (
            <Stack vertical gap={16}>
              <Card>
                <Card.Section>
                  <Stack vertical gap={10}>
                    <Body weight="semibold">Summary</Body>
                    <Stack gap={8} wrap>
                      <Chip
                        color="default"
                        label={`${preview.invoices_scanned} invoices scanned`}
                        size="small"
                      />
                      {Object.entries(preview.stats).map(([key, value]) =>
                        value > 0 ? (
                          <Chip
                            key={key}
                            color="default"
                            label={`${humanizeLabel(key)} ${value}`}
                            size="small"
                          />
                        ) : null,
                      )}
                    </Stack>
                    <Small>
                      Mode:{' '}
                      {isExecuted ? 'Executed changes' : 'Dry run preview only'}
                    </Small>
                  </Stack>
                </Card.Section>
              </Card>

              {!hasActions && (
                <Card>
                  <Card.Section>
                    <EmptyState title="No reconciliation actions needed — everything is in sync" />
                  </Card.Section>
                </Card>
              )}

              {hasActions && (
                <Card>
                  <Card.Section>
                    <Body weight="semibold">
                      {isExecuted ? 'Actions executed' : 'Proposed actions'} (
                      {actions.length})
                    </Body>
                  </Card.Section>
                  <Card.Section>
                    <Stack vertical gap={8}>
                      {actions.map((action: ReconcileAction, idx: number) => (
                        <div
                          key={idx}
                          style={{
                            border: '1px solid #eaecf0',
                            borderRadius: 10,
                            padding: 12,
                            background: '#f8fafc',
                          }}
                        >
                          <Stack gap={8} align="center" wrap>
                            <Chip
                              color={
                                isExecuted
                                  ? 'success'
                                  : action.action === 'error'
                                    ? 'error'
                                    : 'warning'
                              }
                              label={actionLabel(action.action)}
                              size="small"
                            />
                            <Small>Invoice #{action.invoice_id}</Small>
                            {action.stripe_invoice_id && (
                              <Small>Stripe {action.stripe_invoice_id}</Small>
                            )}
                          </Stack>
                          <Stack vertical gap={4} css={{ marginTop: 8 }}>
                            {Object.keys(action.details || {}).length > 0 ? (
                              Object.entries(action.details).map(
                                ([key, value]) => (
                                  <Small key={key}>
                                    <strong>{humanizeLabel(key)}:</strong>{' '}
                                    {formatReconcileDetailValue(value)}
                                  </Small>
                                ),
                              )
                            ) : (
                              <Small>No extra details provided.</Small>
                            )}
                          </Stack>
                        </div>
                      ))}
                    </Stack>
                  </Card.Section>
                </Card>
              )}

              <Stack justify="end" gap={12}>
                <Button
                  a11yLabel="Close"
                  appearance="outline"
                  label={isExecuted ? 'Done' : 'Cancel'}
                  disabled={executing}
                  type="button"
                  onClick={onClose}
                />
                {hasActions && !isExecuted && (
                  <Button
                    a11yLabel="Execute reconciliation"
                    label="Execute reconciliation"
                    isLoading={executing}
                    type="button"
                    onClick={onExecute}
                  />
                )}
              </Stack>
            </Stack>
          )}
        </Card.Section>
      </Card>
    </Modal>
  );
};

type InvoiceStripeModalProps = {
  isOpen: boolean;
  loading: boolean;
  error: string | null;
  account: CycleAccount | null;
  invoiceDetail: DeepInvoiceDetail | null;
  onClose: () => void;
};

type ReconcileState = {
  accountId: number;
  accountName: string;
} | null;

type DetailState = CycleAccount | null;

type AccountDetailModalProps = {
  isOpen: boolean;
  loading: boolean;
  error: string | null;
  account: CycleAccount | null;
  accountDetail: DeepAccountDetail | null;
  onClose: () => void;
};

const AccountDetailModal: React.FC<AccountDetailModalProps> = ({
  isOpen,
  loading,
  error,
  account,
  accountDetail,
  onClose,
}) => {
  if (!isOpen || !account) return null;

  const summaryEntries = Object.entries(accountDetail?.summary || {});
  const duplicateInvoices =
    accountDetail?.duplicate_local_invoices_by_stripe_invoice_id || [];
  const duplicateItems =
    accountDetail?.duplicate_local_invoice_items_by_stripe_invoice_item_id ||
    [];

  return (
    <Modal
      disableClickout
      size="lg"
      title={`Account details for ${account.account_name}`}
      onRequestClose={onClose}
    >
      <Card>
        <Card.Section>
          <Stack vertical gap={6}>
            <Body weight="semibold">
              Review invoice coverage for this account
            </Body>
            <Small>
              Customer {account.customer_name} (#{account.customer_id}) and
              account #{account.account_id}
            </Small>
          </Stack>
        </Card.Section>

        <Card.Section>
          {loading && <LoadingState text="Loading account details..." />}
          {!loading && error && (
            <div style={{ color: '#b42318', fontSize: 14 }}>{error}</div>
          )}
          {!loading && accountDetail && (
            <Stack vertical gap={16}>
              <Card>
                <Card.Section>
                  <Stack vertical gap={10}>
                    <Body weight="semibold">Summary</Body>
                    <Stack gap={8} wrap>
                      <Chip
                        color="default"
                        label={`${accountDetail.analyzed_timesheet_ids.length} timesheets analyzed`}
                        size="small"
                      />
                      <Chip
                        color="default"
                        label={`${accountDetail.invoices.length} related invoices`}
                        size="small"
                      />
                      <Chip
                        color={
                          duplicateInvoices.length > 0 ? 'warning' : 'success'
                        }
                        label={`${duplicateInvoices.length} duplicate invoices`}
                        size="small"
                      />
                      <Chip
                        color={
                          duplicateItems.length > 0 ? 'warning' : 'success'
                        }
                        label={`${duplicateItems.length} duplicate invoice items`}
                        size="small"
                      />
                    </Stack>
                    {summaryEntries.length > 0 && (
                      <Stack gap={8} wrap>
                        {summaryEntries.map(([status, count]) => (
                          <Chip
                            key={status}
                            color="default"
                            label={`${humanizeLabel(status)} ${count}`}
                            size="small"
                          />
                        ))}
                      </Stack>
                    )}
                  </Stack>
                </Card.Section>
              </Card>

              <Card>
                <Card.Section>
                  <Body weight="semibold">
                    Timesheets ({accountDetail.timesheets.length})
                  </Body>
                </Card.Section>
                <Card.Section>
                  <Stack vertical gap={8}>
                    {accountDetail.timesheets.length === 0 ? (
                      <Small>No timesheets returned for this account.</Small>
                    ) : (
                      accountDetail.timesheets.map((timesheetDetail) => (
                        <div
                          key={timesheetDetail.timesheet.id}
                          style={{
                            border: '1px solid #eaecf0',
                            borderRadius: 10,
                            padding: 12,
                            background: '#f8fafc',
                          }}
                        >
                          <Stack gap={8} align="center" wrap>
                            <Body weight="semibold">
                              Timesheet {timesheetDetail.timesheet.id}
                            </Body>
                            <Chip
                              color={statusColor(timesheetDetail.status)}
                              label={humanizeLabel(timesheetDetail.status)}
                              size="small"
                            />
                            <Chip
                              color="default"
                              label={humanizeLabel(
                                timesheetDetail.recommended_action,
                              )}
                              size="small"
                            />
                          </Stack>
                          <Stack vertical gap={4} css={{ marginTop: 8 }}>
                            <Small>
                              Active invoice items:{' '}
                              {timesheetDetail.active_invoice_items.length}
                            </Small>
                            {timesheetDetail.net_time_analysis && (
                              <Small>
                                Net time:{' '}
                                {
                                  timesheetDetail.net_time_analysis
                                    .expected_net_time
                                }
                                min expected,{' '}
                                {
                                  timesheetDetail.net_time_analysis
                                    .actual_net_time
                                }
                                min actual
                              </Small>
                            )}
                            {timesheetDetail.issues.length > 0 && (
                              <Stack gap={8} wrap>
                                {timesheetDetail.issues.map((issue) => (
                                  <Chip
                                    key={issue}
                                    color="warning"
                                    label={humanizeLabel(issue)}
                                    size="small"
                                  />
                                ))}
                              </Stack>
                            )}
                          </Stack>
                        </div>
                      ))
                    )}
                  </Stack>
                </Card.Section>
              </Card>

              <Card>
                <Card.Section>
                  <Body weight="semibold">
                    Related invoices ({accountDetail.invoices.length})
                  </Body>
                </Card.Section>
                <Card.Section>
                  <Stack vertical gap={8}>
                    {accountDetail.invoices.length === 0 ? (
                      <Small>No related invoices found.</Small>
                    ) : (
                      accountDetail.invoices.map((invoice) => (
                        <div
                          key={invoice.invoice.id}
                          style={{
                            border: '1px solid #eaecf0',
                            borderRadius: 10,
                            padding: 12,
                            background: '#f8fafc',
                          }}
                        >
                          <Stack gap={8} align="center" wrap>
                            <Body weight="semibold">
                              Invoice #{invoice.invoice.id}
                            </Body>
                            <Chip
                              color="default"
                              label={humanizeLabel(invoice.status)}
                              size="small"
                            />
                            {invoice.invoice.stripe_invoice_id && (
                              <Small>
                                Stripe {invoice.invoice.stripe_invoice_id}
                              </Small>
                            )}
                          </Stack>
                          <Stack vertical gap={4} css={{ marginTop: 8 }}>
                            <Small>
                              Local items: {invoice.local_invoice_items.length}
                            </Small>
                            <Small>
                              Stripe items:{' '}
                              {invoice.stripe_invoice_items.length}
                            </Small>
                            {invoice.issues.length > 0 && (
                              <Stack gap={8} wrap>
                                {invoice.issues.map((issue) => (
                                  <Chip
                                    key={issue}
                                    color="warning"
                                    label={humanizeLabel(issue)}
                                    size="small"
                                  />
                                ))}
                              </Stack>
                            )}
                          </Stack>
                        </div>
                      ))
                    )}
                  </Stack>
                </Card.Section>
              </Card>

              {(duplicateInvoices.length > 0 || duplicateItems.length > 0) && (
                <Card>
                  <Card.Section>
                    <Body weight="semibold">Duplicates</Body>
                  </Card.Section>
                  <Card.Section>
                    <Stack vertical gap={8}>
                      {duplicateInvoices.map((duplicate) => (
                        <Small key={duplicate.stripe_invoice_id}>
                          Stripe invoice {duplicate.stripe_invoice_id}: local
                          invoices {duplicate.invoice_ids.join(', ')}
                        </Small>
                      ))}
                      {duplicateItems.map((duplicate) => (
                        <Small key={duplicate.stripe_invoice_item_id}>
                          Stripe invoice item {duplicate.stripe_invoice_item_id}
                          : local items {duplicate.invoice_item_ids.join(', ')}
                        </Small>
                      ))}
                    </Stack>
                  </Card.Section>
                </Card>
              )}

              <Stack justify="end" gap={12}>
                <Button
                  a11yLabel="Close account details"
                  appearance="outline"
                  label="Close"
                  type="button"
                  onClick={onClose}
                />
              </Stack>
            </Stack>
          )}
        </Card.Section>
      </Card>
    </Modal>
  );
};

const InvoiceStripeModal: React.FC<InvoiceStripeModalProps> = ({
  isOpen,
  loading,
  error,
  account,
  invoiceDetail,
  onClose,
}) => {
  if (!isOpen) return null;

  return (
    <Modal
      disableClickout
      size="lg"
      title={
        account
          ? `Invoice details for ${account.account_name}`
          : 'Invoice details'
      }
      onRequestClose={onClose}
    >
      <Card>
        <Card.Section>
          <Stack vertical gap={6}>
            <Body weight="semibold">
              Compare local invoice data with Stripe
            </Body>
            {account && (
              <Small>
                Customer {account.customer_name} (#{account.customer_id}) and
                account #{account.account_id}
              </Small>
            )}
          </Stack>
        </Card.Section>

        <Card.Section>
          {loading && <LoadingState text="Loading invoice details..." />}
          {!loading && error && (
            <div style={{ color: '#b42318', fontSize: 14 }}>{error}</div>
          )}
          {!loading && invoiceDetail && (
            <Stack vertical gap={16}>
              <Stack vertical gap={6}>
                <Heading>Invoice {invoiceDetail.invoice.id}</Heading>
                <Small>
                  Stripe ID: {invoiceDetail.invoice.stripe_invoice_id || 'n/a'}
                </Small>
                <Chip
                  color="default"
                  label={humanizeLabel(invoiceDetail.status)}
                  size="small"
                />
              </Stack>

              {invoiceDetail.issues.length > 0 && (
                <Stack gap={8} wrap>
                  {invoiceDetail.issues.map((issue) => (
                    <Chip
                      key={issue}
                      color="warning"
                      label={humanizeStatus(issue)}
                      size="small"
                    />
                  ))}
                </Stack>
              )}

              <Card>
                <Card.Section>
                  <Stack vertical gap={8}>
                    <Small>
                      Local invoice items:{' '}
                      {invoiceDetail.local_invoice_items.length}
                    </Small>
                    <Small>
                      Stripe invoice items:{' '}
                      {invoiceDetail.stripe_invoice_items.length}
                    </Small>
                    <Small>
                      Missing locally:{' '}
                      {invoiceDetail.stripe_items_missing_locally.length}
                    </Small>
                    <Small>
                      Missing in Stripe:{' '}
                      {invoiceDetail.local_items_missing_in_stripe.length}
                    </Small>
                  </Stack>
                </Card.Section>
              </Card>

              {invoiceDetail.local_invoice_items.length > 0 && (
                <Card>
                  <Card.Section>
                    <Body weight="semibold">Local invoice items</Body>
                  </Card.Section>
                  <Card.Section>
                    <Stack vertical gap={6}>
                      {invoiceDetail.local_invoice_items
                        .slice(0, 15)
                        .map((item) => (
                          <Small key={item.id}>
                            #{item.id} timesheet {item.timesheet_id || 'n/a'}{' '}
                            stripe {item.stripe_invoice_item_id || 'blank'}
                          </Small>
                        ))}
                    </Stack>
                  </Card.Section>
                </Card>
              )}

              {invoiceDetail.stripe_invoice_items.length > 0 && (
                <Card>
                  <Card.Section>
                    <Body weight="semibold">Stripe invoice items</Body>
                  </Card.Section>
                  <Card.Section>
                    <Stack vertical gap={6}>
                      {invoiceDetail.stripe_invoice_items
                        .slice(0, 15)
                        .map((item) => (
                          <Small key={item.id}>
                            {item.id} amount $
                            {((item.amount || 0) / 100).toFixed(2)}{' '}
                            {item.description || 'no description'}
                          </Small>
                        ))}
                    </Stack>
                  </Card.Section>
                </Card>
              )}

              <Stack justify="end" gap={12}>
                <Button
                  a11yLabel="Close invoice details"
                  appearance="outline"
                  label="Close"
                  type="button"
                  onClick={onClose}
                />
              </Stack>
            </Stack>
          )}
        </Card.Section>
      </Card>
    </Modal>
  );
};

type AccountCardProps = {
  account: CycleAccount;
  loadingTimesheetId: number | null;
  loadingRemoveItemId: number | null;
  loadingCreateAccountId: number | null;
  loadingVoidInvoiceId: number | null;
  onAddLineItem: (account: CycleAccount, timesheet: CycleTimesheet) => void;
  onRemoveLineItem: (account: CycleAccount, timesheet: CycleTimesheet) => void;
  onInspectInvoice: (account: CycleAccount) => void;
  onCreateInvoice: (account: CycleAccount) => void;
  onVoidInvoice: (account: CycleAccount) => void;
  onReconcile: (account: CycleAccount) => void;
};

const AccountCard: React.FC<AccountCardProps> = ({
  account,
  loadingTimesheetId,
  loadingRemoveItemId,
  loadingCreateAccountId,
  loadingVoidInvoiceId,
  onAddLineItem,
  onRemoveLineItem,
  onInspectInvoice,
  onCreateInvoice,
  onVoidInvoice,
  onReconcile,
}) => {
  const invoiceUrl = account.invoice
    ? `/${ROUTES.invoices}/${account.invoice.id}`
    : null;
  const stripeReference =
    account.invoice?.number || account.invoice?.stripe_invoice_id || null;
  const [isExpanded, setIsExpanded] = useState(false);

  const canVoid =
    account.invoice &&
    account.invoice.status === 'open' &&
    !account.duplicate_invoice;

  return (
    <Card>
      <Card.Section>
        <Stack justify="apart" align="start" gap={16}>
          <Stack vertical gap={6} css={{ flex: 1, minWidth: 0 }}>
            <Stack gap={8} align="center" wrap>
              <Heading>
                {account.customer_name} - {account.account_name}
              </Heading>
              <Chip
                color="default"
                label={`${account.timesheets.length} timesheets`}
                size="small"
              />
              {account.duplicate_invoice && (
                <Chip color="error" label="Duplicate invoice" size="small" />
              )}
            </Stack>
            {!isExpanded && (
              <Small>
                {account.invoice
                  ? `Invoice #${account.invoice.id}`
                  : 'No invoice created yet'}
              </Small>
            )}
            {isExpanded && (
              <>
                <Stack gap={10} wrap>
                  <div
                    style={{
                      padding: '8px 12px',
                      borderRadius: 10,
                      background: '#f8fafc',
                      border: '1px solid #e4e7ec',
                      minWidth: 180,
                    }}
                  >
                    <Small>Customer</Small>
                    <Body weight="semibold">{account.customer_name}</Body>
                    <Small>ID {account.customer_id}</Small>
                  </div>
                  <div
                    style={{
                      padding: '8px 12px',
                      borderRadius: 10,
                      background: '#f8fafc',
                      border: '1px solid #e4e7ec',
                      minWidth: 160,
                    }}
                  >
                    <Small>Account</Small>
                    <Body weight="semibold">{account.account_name}</Body>
                    <Small>ID {account.account_id}</Small>
                  </div>
                </Stack>
                <Stack gap={8} align="center" wrap>
                  {account.invoice ? (
                    <>
                      <Stack vertical gap={2}>
                        <Small>
                          Invoice:{' '}
                          <Link to={invoiceUrl!}>#{account.invoice.id}</Link>
                        </Small>
                        {stripeReference && (
                          <Small>Stripe reference: {stripeReference}</Small>
                        )}
                      </Stack>
                      <Chip
                        color="default"
                        label={account.invoice.status}
                        size="small"
                      />
                    </>
                  ) : (
                    <Small>No invoice created yet</Small>
                  )}
                </Stack>
              </>
            )}
          </Stack>

          <Stack inline>
            <IconicButton
              a11yLabel={`${isExpanded ? 'Collapse' : 'Expand'} account ${account.account_id}`}
              appearance="clear"
              css={{ fontSize: '1rem', marginLeft: 'auto' }}
              icon={isExpanded ? faChevronUp : faChevronRight}
              iconTheme="theme"
              rounded
              size="sm"
              type="button"
              onClick={() => setIsExpanded((current) => !current)}
            />
          </Stack>
        </Stack>
      </Card.Section>

      {isExpanded && (
        <Card.Section>
          <Stack gap={8} wrap>
            {(!account.invoice || account.invoice.status === 'void') && (
              <Button
                a11yLabel={`Create invoice for account ${account.account_id}`}
                appearance="secondary"
                label="Create invoice"
                isLoading={loadingCreateAccountId === account.account_id}
                type="button"
                onClick={() => onCreateInvoice(account)}
              />
            )}
            {canVoid && (
              <Button
                a11yLabel={`Void invoice for account ${account.account_id}`}
                appearance="outline"
                label="Void invoice"
                isLoading={loadingVoidInvoiceId === account.invoice!.id}
                type="button"
                onClick={() => onVoidInvoice(account)}
              />
            )}
            {account.invoice && (
              <Button
                a11yLabel={`Reconcile invoice for account ${account.account_id}`}
                appearance="secondary"
                label="Reconcile"
                type="button"
                onClick={() => onReconcile(account)}
              />
            )}
            <Button
              a11yLabel={`Explore invoice details for account ${account.account_id}`}
              appearance="secondary"
              label="Explore more details"
              type="button"
              onClick={() => onInspectInvoice(account)}
            />
          </Stack>
        </Card.Section>
      )}

      {isExpanded && (
        <Card.Section>
          <div style={{ overflowX: 'auto' }}>
            <Table>
              <TableHeader
                fields={[
                  { name: 'TIMESHEET', style: { width: 90 } },
                  { name: 'WORKER', style: { width: 180 } },
                  { name: 'STATUS', style: { width: 180 } },
                  { name: 'APPROVED AT', style: { width: 210 } },
                  { name: 'TOTAL AMOUNT', style: { width: 140 } },
                  { name: 'LINE ITEM', style: { width: 220 } },
                  { name: 'IN CURRENT INVOICE', style: { width: 170 } },
                  { name: '', style: { width: 160 } },
                ]}
              />
              <tbody>
                {account.timesheets.map((timesheet) => {
                  const timesheetUrl =
                    timesheet.order_id != null && timesheet.job_id != null
                      ? `/${ROUTES.orders}/${timesheet.order_id}/jobs/${timesheet.job_id}`
                      : null;
                  const linkedInvoiceUrl = timesheet.linked_invoice_id
                    ? `/${ROUTES.invoices}/${timesheet.linked_invoice_id}`
                    : null;
                  const actionDisabled =
                    !timesheet.can_add_line_item ||
                    loadingTimesheetId === timesheet.id;
                  const actionLabel = account.duplicate_invoice
                    ? 'Duplicate invoice'
                    : timesheet.included_in_current_invoice
                      ? 'Included'
                      : timesheet.status === 'linked_to_other_invoice'
                        ? `Linked to ${timesheet.linked_invoice_number || `#${timesheet.linked_invoice_id}`}`
                        : timesheet.status === 'requires_review'
                          ? 'Needs review'
                          : 'Add line item';

                  return (
                    <TableRow key={timesheet.id}>
                      <TableCell>
                        {timesheetUrl ? (
                          <Link css={linkedTextCss} to={timesheetUrl}>
                            <Body weight="semibold">{timesheet.id}</Body>
                          </Link>
                        ) : (
                          <Body css={linkedTextCss} weight="semibold">
                            {timesheet.id}
                          </Body>
                        )}
                      </TableCell>
                      <TableCell>
                        {timesheet.worker_id && timesheet.worker_name ? (
                          <Link
                            css={linkedTextCss}
                            to={`/${ROUTES.workersV2}/${timesheet.worker_id}`}
                          >
                            <Small>{timesheet.worker_name}</Small>
                          </Link>
                        ) : (
                          <Small>{timesheet.worker_name || 'n/a'}</Small>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          color={statusColor(timesheet.status)}
                          label={humanizeStatus(timesheet.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Small>{formatDateTime(timesheet.approved_at)}</Small>
                      </TableCell>
                      <TableCell>
                        <Small>
                          {formatCurrency(timesheet.total_amount_cents)}
                        </Small>
                      </TableCell>
                      <TableCell>
                        <Stack vertical gap={2}>
                          <Small>
                            Local: {timesheet.invoice_line_item_id ?? 'n/a'}
                          </Small>
                          {timesheet.stripe_invoice_item_id && (
                            <Small>
                              Stripe: {timesheet.stripe_invoice_item_id}
                            </Small>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          color={
                            timesheet.included_in_current_invoice
                              ? 'success'
                              : 'warning'
                          }
                          label={
                            timesheet.included_in_current_invoice ? 'Yes' : 'No'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Stack gap={4}>
                          {timesheet.status === 'linked_to_other_invoice' &&
                          linkedInvoiceUrl ? (
                            <Link css={linkedTextCss} to={linkedInvoiceUrl}>
                              <Small>{actionLabel}</Small>
                            </Link>
                          ) : timesheet.included_in_current_invoice ? (
                            <Button
                              a11yLabel={`Remove line item for timesheet ${timesheet.id}`}
                              appearance="outline"
                              label="Remove"
                              isLoading={
                                loadingRemoveItemId ===
                                timesheet.invoice_line_item_id
                              }
                              type="button"
                              onClick={() =>
                                onRemoveLineItem(account, timesheet)
                              }
                            />
                          ) : (
                            <Button
                              a11yLabel={`${actionLabel} for timesheet ${timesheet.id}`}
                              appearance="secondary"
                              label={actionLabel}
                              disabled={actionDisabled}
                              isLoading={loadingTimesheetId === timesheet.id}
                              type="button"
                              onClick={() => onAddLineItem(account, timesheet)}
                            />
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </Card.Section>
      )}
    </Card>
  );
};

const InvoiceHealth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem(INVOICE_HEALTH_AUTH_KEY) === 'true';
  });
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [selectedCycle, setSelectedCycle] = useState<InvoiceCycle | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [accountFilter, setAccountFilter] = useState<AccountFilter>('all');
  const [reconcileState, setReconcileState] = useState<ReconcileState>(null);
  const [detailState, setDetailState] = useState<DetailState>(null);
  const cycles = useCycles();
  const cycleAccounts = useCycleAccounts();
  const addLineItem = useAddLineItem();
  const removeLineItem = useRemoveLineItem();
  const createInvoice = useCreateInvoice();
  const voidInvoice = useVoidInvoice();
  const reconcile = useReconcile();
  const accountDetail = useAccountDetail();
  const invoiceDetail = useInvoiceDetail();
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const cycleParams = useMemo(
    () =>
      selectedCycle
        ? {
            from: selectedCycle.from,
            to: selectedCycle.to,
            referenceTime: selectedCycle.started_at,
          }
        : undefined,
    [selectedCycle],
  );

  const refreshAccounts = useCallback(() => {
    if (!selectedCycle) return;
    cycleAccounts.fetch({
      from: selectedCycle.from,
      to: selectedCycle.to,
      referenceTime: selectedCycle.started_at,
    });
  }, [selectedCycle, cycleAccounts.fetch]);

  useEffect(() => {
    cycles.fetch();
  }, [cycles.fetch]);

  useEffect(() => {
    if (selectedCycle) {
      refreshAccounts();
    }
  }, [selectedCycle, refreshAccounts]);

  useEffect(() => {
    if (!reconcileState) return;

    reconcile.fetchPreview(reconcileState.accountId, cycleParams);
  }, [cycleParams, reconcile.fetchPreview, reconcileState]);

  useEffect(() => {
    if (!detailState) return;

    if (detailState.invoice) {
      invoiceDetail.inspectInvoice(detailState.invoice.id);
      return;
    }

    accountDetail.inspectAccount(
      detailState.account_id,
      detailState.timesheets.map((timesheet) => timesheet.id),
    );
  }, [accountDetail.inspectAccount, detailState, invoiceDetail.inspectInvoice]);

  const handleCycleSelect = useCallback(
    (cycle: InvoiceCycle) => {
      cycleAccounts.clear();
      setSelectedCycle(cycle);
      setSearchQuery('');
      setAccountFilter('all');
      setReconcileState(null);
      setDetailState(null);
      addLineItem.clearError();
      removeLineItem.clearError();
      createInvoice.clearError();
      voidInvoice.clearError();
      reconcile.clear();
      accountDetail.clear();
      invoiceDetail.clear();
    },
    [
      addLineItem.clearError,
      accountDetail.clear,
      cycleAccounts.clear,
      removeLineItem.clearError,
      createInvoice.clearError,
      invoiceDetail.clear,
      voidInvoice.clearError,
      reconcile.clear,
    ],
  );

  const handleAddLineItem = useCallback(
    async (_account: CycleAccount, timesheet: CycleTimesheet) => {
      if (!selectedCycle) return;

      addLineItem.clearError();
      const result = await addLineItem.sync({
        timesheetId: timesheet.id,
        from: selectedCycle.from,
        to: selectedCycle.to,
        referenceTime: selectedCycle.started_at,
      });

      if (result.success) {
        refreshAccounts();
      } else {
        window.alert(result.error || 'Failed to add line item');
      }
    },
    [selectedCycle, addLineItem.sync, addLineItem.clearError, refreshAccounts],
  );

  const handleRemoveLineItem = useCallback(
    async (_account: CycleAccount, timesheet: CycleTimesheet) => {
      if (!timesheet.invoice_line_item_id) return;

      const confirmed = window.confirm(
        `Remove line item for timesheet #${timesheet.id} (${timesheet.worker_name || 'Unknown'})?\n\nThis will delete the line item from Stripe and update the invoice total.`,
      );
      if (!confirmed) return;

      removeLineItem.clearError();
      const result = await removeLineItem.remove(
        timesheet.invoice_line_item_id,
      );

      if (result.success) {
        refreshAccounts();
      } else {
        window.alert(result.error || 'Failed to remove line item');
      }
    },
    [removeLineItem.remove, removeLineItem.clearError, refreshAccounts],
  );

  const handleCreateInvoice = useCallback(
    async (account: CycleAccount) => {
      if (!selectedCycle) return;

      createInvoice.clearError();
      const result = await createInvoice.create({
        accountId: account.account_id,
        referenceTime: selectedCycle.started_at,
      });

      if (result.success) {
        refreshAccounts();
      } else {
        window.alert(result.error || 'Failed to create invoice');
      }
    },
    [
      selectedCycle,
      createInvoice.create,
      createInvoice.clearError,
      refreshAccounts,
    ],
  );

  const handleVoidInvoice = useCallback(
    async (account: CycleAccount) => {
      if (!account.invoice) return;

      const confirmed = window.confirm(
        `Void invoice #${account.invoice.id} for ${account.account_name}? This will void the invoice in Stripe and reset linked timesheets.`,
      );
      if (!confirmed) return;

      voidInvoice.clearError();
      const success = await voidInvoice.voidInvoice(account.invoice.id);

      if (success) {
        refreshAccounts();
      }
    },
    [voidInvoice.voidInvoice, voidInvoice.clearError, refreshAccounts],
  );

  const handleInspectInvoice = useCallback(
    (account: CycleAccount) => {
      accountDetail.clear();
      invoiceDetail.clear();
      setDetailState(account);
    },
    [accountDetail.clear, invoiceDetail.clear],
  );

  const handleDetailClose = useCallback(() => {
    accountDetail.clear();
    invoiceDetail.clear();
    setDetailState(null);
  }, [accountDetail.clear, invoiceDetail.clear]);

  const handleReconcile = useCallback(
    (account: CycleAccount) => {
      reconcile.clear();
      setReconcileState({
        accountId: account.account_id,
        accountName: account.account_name,
      });
    },
    [reconcile.clear],
  );

  const handleReconcileClose = useCallback(() => {
    if (reconcile.executing) return;

    reconcile.clear();
    setReconcileState(null);
  }, [reconcile.clear, reconcile.executing]);

  const handleReconcileExecute = useCallback(async () => {
    if (!reconcileState) return;

    const executeResult = await reconcile.execute(
      reconcileState.accountId,
      cycleParams,
    );

    if (executeResult.success) {
      refreshAccounts();
    }
  }, [cycleParams, reconcile.execute, reconcileState, refreshAccounts]);

  const accounts = useMemo(
    () => cycleAccounts.data?.accounts || [],
    [cycleAccounts.data],
  );
  const filteredAccounts = useMemo(() => {
    const normalizedQuery = deferredSearchQuery.trim().toLowerCase();
    return accounts.filter((account) => {
      const accountName = account.account_name.toLowerCase();
      const customerName = account.customer_name.toLowerCase();
      const accountId = String(account.account_id);
      const matchesSearch =
        accountName.includes(normalizedQuery) ||
        customerName.includes(normalizedQuery) ||
        accountId.includes(normalizedQuery);

      if (!matchesSearch) return false;

      if (accountFilter === 'all') return true;
      if (accountFilter === 'no_invoice') return account.invoice == null;

      // An account is complete when it has an invoice, no duplicates,
      // and every actionable timesheet (not_included) has been synced.
      // Rejected/held/not_invoiceable timesheets don't count as missing.
      const actionableMissing = account.timesheets.filter(
        (ts) => ts.status === 'not_included',
      ).length;
      const isComplete =
        account.invoice != null &&
        !account.duplicate_invoice &&
        actionableMissing === 0;

      if (accountFilter === 'complete') return isComplete;
      // Incomplete = has invoice but not complete, OR has no invoice
      return !isComplete;
    });
  }, [accounts, accountFilter, deferredSearchQuery]);
  const actionError =
    addLineItem.error ||
    removeLineItem.error ||
    createInvoice.error ||
    voidInvoice.error;
  const isRefreshingCycleAccounts =
    selectedCycle != null &&
    cycleAccounts.loading &&
    cycleAccounts.data != null;

  const handlePasswordSubmit = useCallback(
    (event?: React.FormEvent | React.MouseEvent) => {
      if (event) event.preventDefault();

      if (password === INVOICE_HEALTH_PASSWORD) {
        setIsAuthenticated(true);
        setAuthError('');
        localStorage.setItem(INVOICE_HEALTH_AUTH_KEY, 'true');
        return;
      }

      setAuthError('Incorrect password');
      setPassword('');
    },
    [password],
  );

  if (!isAuthenticated) {
    return (
      <Page title="Invoice Health" size="sm">
        <Card>
          <Card.Section>
            <form onSubmit={handlePasswordSubmit}>
              <Stack vertical gap={16}>
                <div>
                  <Heading>Invoice Health</Heading>
                  <Small>Enter the page password to continue.</Small>
                </div>
                <div>
                  <input
                    autoFocus
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="Enter password"
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: 8,
                      border: '1px solid #d0d5dd',
                      background: '#fff',
                      fontSize: 14,
                      color: '#344054',
                    }}
                  />
                </div>
                {authError && (
                  <div style={{ color: '#b42318', fontSize: 14 }}>
                    {authError}
                  </div>
                )}
                <Stack justify="end">
                  <Button
                    a11yLabel="Unlock invoice health"
                    label="Continue"
                    type="submit"
                    onClick={handlePasswordSubmit}
                  />
                </Stack>
              </Stack>
            </form>
          </Card.Section>
        </Card>
      </Page>
    );
  }

  return (
    <Page title="Invoice Health" size="xl">
      <Stack vertical gap={20}>
        <Card>
          <Card.Section>
            <Stack vertical gap={12}>
              <div>
                <Heading>Invoice Health</Heading>
                <Small>
                  Select a cycle to review approved timesheets by account
                </Small>
              </div>

              {cycles.loading ? (
                <Skeleton.Body lines={2} />
              ) : cycles.error ? (
                <div style={{ color: '#b42318', fontSize: 14 }}>
                  {cycles.error}
                </div>
              ) : (
                <Stack vertical gap={12}>
                  <CycleDropdown
                    cycles={cycles.cycles}
                    selectedCycle={selectedCycle}
                    onSelect={handleCycleSelect}
                  />
                  {selectedCycle && (
                    <Stack gap={12} wrap>
                      <input
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                        placeholder="Search account id, account name, or customer name"
                        style={{
                          width: '100%',
                          maxWidth: 420,
                          padding: '10px 14px',
                          borderRadius: 8,
                          border: '1px solid #d0d5dd',
                          background: '#fff',
                          fontSize: 14,
                          color: '#344054',
                        }}
                      />
                      <select
                        value={accountFilter}
                        onChange={(event) =>
                          setAccountFilter(event.target.value as AccountFilter)
                        }
                        style={{
                          width: '100%',
                          maxWidth: 220,
                          padding: '10px 14px',
                          borderRadius: 8,
                          border: '1px solid #d0d5dd',
                          background: '#fff',
                          fontSize: 14,
                          fontWeight: 500,
                          color: '#344054',
                          cursor: 'pointer',
                          appearance: 'none',
                        }}
                      >
                        <option value="all">All statuses</option>
                        <option value="complete">Complete</option>
                        <option value="incomplete">Incomplete</option>
                        <option value="no_invoice">No invoice</option>
                      </select>
                    </Stack>
                  )}
                </Stack>
              )}
            </Stack>
          </Card.Section>
        </Card>

        {actionError && (
          <Card>
            <Card.Section>
              <div style={{ color: '#b42318', fontSize: 14 }}>
                {actionError}
              </div>
            </Card.Section>
          </Card>
        )}

        {!selectedCycle && !cycles.loading && (
          <Card>
            <Card.Section>
              <EmptyState title="Select a cycle to load accounts and approved timesheets" />
            </Card.Section>
          </Card>
        )}

        {selectedCycle && cycleAccounts.loading && !cycleAccounts.data && (
          <LoadingState text="Loading accounts and timesheets..." />
        )}

        {isRefreshingCycleAccounts && selectedCycle && (
          <Card>
            <Card.Section>
              <LoadingState
                overlayColor="white"
                text={`Refreshing ${selectedCycle.date_label} accounts and timesheets...`}
                css={{ minHeight: 120 }}
              />
            </Card.Section>
          </Card>
        )}

        {selectedCycle && cycleAccounts.error && (
          <Card>
            <Card.Section>
              <div style={{ color: '#b42318', fontSize: 14 }}>
                {cycleAccounts.error}
              </div>
            </Card.Section>
          </Card>
        )}

        {selectedCycle && cycleAccounts.data && (
          <>
            {accounts.length === 0 ? (
              <Card>
                <Card.Section>
                  <EmptyState title="No approved timesheets found in this cycle" />
                </Card.Section>
              </Card>
            ) : filteredAccounts.length === 0 ? (
              <Card>
                <Card.Section>
                  <EmptyState title="No accounts match that search" />
                </Card.Section>
              </Card>
            ) : (
              <Stack vertical gap={20}>
                {filteredAccounts.map((account) => (
                  <AccountCard
                    key={account.account_id}
                    account={account}
                    loadingTimesheetId={addLineItem.loadingTimesheetId}
                    loadingRemoveItemId={removeLineItem.loadingItemId}
                    loadingCreateAccountId={createInvoice.loadingAccountId}
                    loadingVoidInvoiceId={voidInvoice.loadingInvoiceId}
                    onAddLineItem={handleAddLineItem}
                    onRemoveLineItem={handleRemoveLineItem}
                    onInspectInvoice={handleInspectInvoice}
                    onCreateInvoice={handleCreateInvoice}
                    onVoidInvoice={handleVoidInvoice}
                    onReconcile={handleReconcile}
                  />
                ))}
              </Stack>
            )}
          </>
        )}

        <ReconcileModal
          isOpen={reconcileState != null}
          accountName={reconcileState?.accountName || null}
          accountId={reconcileState?.accountId || null}
          preview={reconcile.preview}
          loading={reconcile.loading}
          executing={reconcile.executing}
          error={reconcile.error}
          onClose={handleReconcileClose}
          onExecute={handleReconcileExecute}
        />
        <AccountDetailModal
          isOpen={detailState != null && detailState.invoice == null}
          loading={accountDetail.loading}
          error={accountDetail.error}
          account={detailState}
          accountDetail={accountDetail.accountDetail}
          onClose={handleDetailClose}
        />
        <InvoiceStripeModal
          isOpen={detailState?.invoice != null}
          loading={invoiceDetail.loading}
          error={invoiceDetail.error}
          account={detailState}
          invoiceDetail={invoiceDetail.invoiceDetail}
          onClose={handleDetailClose}
        />
      </Stack>
    </Page>
  );
};

export default InvoiceHealth;
